import { SupportUserAvailabilityOverride } from "@redotech/cockroachdb-codegen/tables/support-v2/support-user-availability-override";
import { MongoUserId } from "@redotech/effect-api-shared/ids/mongo-ids";
import PhoneCallIcon from "@redotech/redo-icons/phone-call.svg";
import PhoneXIcon from "@redotech/redo-icons/phone-x.svg";
import GearIcon from "@redotech/redo-icons/settings-02.svg";
import { useTeamUsers } from "@redotech/redo-merchant-app-common/hooks/use-team-users";
import { useSetAvailabilityOverrideMutation } from "@redotech/redo-merchant-app-common/setting/use-work-schedule-query";
import {
  RedoBadge,
  type RedoBadgeColor,
} from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  type RedoListItem,
  type RedoListItemSelectedSource,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ActiveCallSectionV2 } from "./active-call-section-v2";
import { PhoneIdleV2 } from "./phone-idle-v2";
import {
  type ConnectionStatus,
  type DebugTelnyxNotification,
  type MicPermission,
  VoiceContextV2,
} from "./voice-context-v2";

const SESSION_LABEL: Record<ConnectionStatus, string> = {
  disconnected: "Telnyx: offline",
  "fetching-token": "Telnyx: signing in…",
  connecting: "Telnyx: connecting…",
  ready: "Telnyx: ready",
  error: "Telnyx: error",
};

const SESSION_BADGE_COLOR: Record<ConnectionStatus, RedoBadgeColor> = {
  disconnected: "gray",
  "fetching-token": "warning",
  connecting: "warning",
  ready: "success",
  error: "error",
};

const MIC_LABEL: Record<MicPermission, string> = {
  unknown: "Mic: checking…",
  granted: "Mic: ready",
  prompt: "Mic: not granted",
  denied: "Mic: denied",
};

const MIC_BADGE_COLOR: Record<MicPermission, RedoBadgeColor> = {
  unknown: "gray",
  granted: "success",
  prompt: "warning",
  denied: "error",
};

type AgentStatusOption = "available" | "unavailable";

const AGENT_STATUS_LABEL: Record<AgentStatusOption, string> = {
  available: "Available",
  unavailable: "Unavailable",
};

interface VoiceUserPanelInnerProps {
  /** Header label rendered above the controls. */
  readonly label?: string;
  /** Show the "acting user" dropdown — dev harness only. Default: false. */
  readonly showImpersonation?: boolean;
  /**
   * Always render the Telnyx + Mic status badges. Dev harness only —
   * production hides them unless there's an actual issue worth
   * surfacing (Telnyx error/disconnected, mic prompt/denied).
   * Default: false.
   */
  readonly showDebugBadges?: boolean;
  /** Show "Remove" button next to the header label. Dev harness only. */
  readonly onRemove?: () => void;
}

/**
 * Pure consumer of `VoiceContextV2` — the call UI itself, no positioning
 * or chrome. Same component is mounted N times inline on the dev page
 * (each in its own provider) and once inside `PhoneWidgetV2` for prod.
 */
export const VoiceUserPanelInner = memo(function VoiceUserPanelInner({
  label,
  showImpersonation = false,
  showDebugBadges = false,
  onRemove,
}: VoiceUserPanelInnerProps) {
  const ctx = useContext(VoiceContextV2);
  if (ctx == null) {
    throw new Error("VoiceContextV2 missing");
  }

  return (
    <Flex dir="column" gap="sm" p="md">
      <Header label={label} onRemove={onRemove} />
      {showImpersonation && <ImpersonationDropdown />}
      <StatusDropdown />
      <BadgeRow showAlways={showDebugBadges} />
      {(() => {
        // Same gating as BadgeRow: in production we only surface
        // Telnyx detail when the operator wanted to be online —
        // otherwise "socket closed" is just the expected effect of
        // going offline.
        const showTelnyxDetail =
          ctx.statusDetail != null && (showDebugBadges || ctx.desiredOnline);
        if (!showTelnyxDetail && !ctx.micError) {
          return null;
        }
        return (
          <Flex dir="column" gap="xxs">
            {showTelnyxDetail && (
              <Text color="subdued" fontSize="xs">
                Telnyx: {ctx.statusDetail}
              </Text>
            )}
            {ctx.micError && (
              <Text color="error" fontSize="xs">
                {ctx.micError}
              </Text>
            )}
          </Flex>
        );
      })()}
      {showDebugBadges && <DebugTelnyxNotificationEvents />}
      <CallSection />
    </Flex>
  );
});

const Header = memo(function Header({
  label,
  onRemove,
}: {
  label?: string;
  onRemove?: () => void;
}) {
  if (!label && !onRemove) {
    return null;
  }
  return (
    <Flex align="center" gap="xs" justify="space-between" w="full">
      {label && (
        <Text fontSize="sm" fontWeight="semibold">
          {label}
        </Text>
      )}
      {onRemove && (
        <RedoButton
          hierarchy="tertiary"
          onClick={onRemove}
          size="xs"
          text="Remove"
        />
      )}
    </Flex>
  );
});

const ImpersonationDropdown = memo(function ImpersonationDropdown() {
  const ctx = useContext(VoiceContextV2);
  if (ctx == null) {
    throw new Error("VoiceContextV2 missing");
  }
  const { data: teamUsers } = useTeamUsers();

  const userOptions: RedoListItem<MongoUserId | null>[] = useMemo(() => {
    const options: RedoListItem<MongoUserId | null>[] = [
      {
        id: "__self__",
        text: "Use my account",
        type: "text" as const,
        value: null,
      },
    ];
    for (const u of teamUsers ?? []) {
      const id = MongoUserId.fromString(String(u._id));
      options.push({ id, text: u.name, type: "text" as const, value: id });
    }
    return options;
  }, [teamUsers]);

  const selectedUserItem = useMemo(
    () =>
      userOptions.find((opt) =>
        ctx.targetMongoUserId === undefined
          ? opt.value === null
          : opt.value === ctx.targetMongoUserId,
      ),
    [userOptions, ctx.targetMongoUserId],
  );

  const onUserSelected = useCallback(
    ({
      item,
    }: {
      item: RedoListItem<MongoUserId | null>;
      source: RedoListItemSelectedSource;
      index: number;
      selected: boolean;
    }) => {
      ctx.setTargetMongoUserId(item.value ?? undefined);
    },
    [ctx],
  );

  return (
    <Flex w="full">
      <RedoSingleSelectDropdownInput
        fullWidth
        options={userOptions}
        optionSelected={onUserSelected}
        placeholder="Acting user"
        selectedItem={selectedUserItem}
        size="xxs"
      />
    </Flex>
  );
});

const StatusDotItem = memo(function StatusDotItem({
  status,
}: {
  status: AgentStatusOption;
}) {
  // No px/py here — menu items get padding from the dropdown's
  // RedoListItemContainer wrapper, and the trigger button supplies its
  // own padding via the xxs size class. Adding more here visibly
  // bloats the closed dropdown.
  return (
    <Flex align="center" gap="sm">
      <RedoBadge
        color={status === "available" ? "success" : "error"}
        size="xs"
        text={AGENT_STATUS_LABEL[status]}
      />
    </Flex>
  );
});

const STATUS_OPTIONS: RedoListItem<AgentStatusOption>[] = (
  ["available", "unavailable"] as const
).map((value) => ({
  id: value,
  type: "custom" as const,
  useWrapper: true,
  value,
  render: <StatusDotItem status={value} />,
}));

const StatusDropdown = memo(function StatusDropdown() {
  const ctx = useContext(VoiceContextV2);
  if (ctx == null) {
    throw new Error("VoiceContextV2 missing");
  }
  const navigate = useNavigate();

  const desired: AgentStatusOption = ctx.desiredOnline
    ? "available"
    : "unavailable";
  const selected = useMemo(
    () => STATUS_OPTIONS.find((opt) => opt.value === desired),
    [desired],
  );
  const setAvailabilityOverride = useSetAvailabilityOverrideMutation();

  const onStatusSelected = useCallback(
    ({
      item,
    }: {
      item: RedoListItem<AgentStatusOption>;
      source: RedoListItemSelectedSource;
      index: number;
      selected: boolean;
    }) => {
      if (item.value === "available") {
        // The same write the schedule UI makes, so going available for calls
        // and going available on the schedule are one backend operation. The
        // override outranks the weekly hours, which is what lets an agent take
        // calls outside their shift by choosing to.
        setAvailabilityOverride.mutate(SupportUserAvailabilityOverride.Active);
        ctx.connect();
      } else {
        // Only disconnect. Clearing the override is no longer a state an agent
        // can choose, and forcing Inactive here would pull them off email and
        // chat too — closing the phone is not a declaration about those.
        // Dropping the device row is what actually stops calls routing here.
        ctx.disconnect();
      }
    },
    [ctx, setAvailabilityOverride],
  );

  return (
    <Flex align="center" gap="xs" w="full">
      <Flex grow={1}>
        <RedoSingleSelectDropdownInput
          fullWidth
          options={STATUS_OPTIONS}
          optionSelected={onStatusSelected}
          placeholder="Status"
          selectedItem={selected}
          size="xxs"
        />
      </Flex>
      <RedoButton
        aria-label="Settings"
        hierarchy="secondary"
        IconLeading={GearIcon}
        onClick={() => navigate("/settings/channels/voice-settings")}
        size="xs"
      />
    </Flex>
  );
});

const BadgeRow = memo(function BadgeRow({
  showAlways,
}: {
  readonly showAlways: boolean;
}) {
  const ctx = useContext(VoiceContextV2);
  if (ctx == null) {
    throw new Error("VoiceContextV2 missing");
  }

  // In production we only surface these badges when something needs
  // the operator's attention. Transient states like "connecting" or
  // "checking…" are normal startup noise and stay hidden. And when
  // the operator has chosen "Unavailable" from the status dropdown,
  // disconnect/error are expected — the dropdown already says so,
  // no need to also flag them as issues.
  const telnyxIssue =
    ctx.desiredOnline &&
    (ctx.connectionStatus === "error" ||
      ctx.connectionStatus === "disconnected");
  const micIssue =
    ctx.micPermission === "prompt" || ctx.micPermission === "denied";

  const showSession = showAlways || telnyxIssue;
  const showMic = showAlways || micIssue;
  const showRequestMic = ctx.micPermission !== "granted" && showMic;

  if (!showSession && !showMic && !showRequestMic) {
    return null;
  }

  return (
    <Flex align="center" gap="xs" wrap="wrap">
      {showSession && (
        <RedoBadge
          color={SESSION_BADGE_COLOR[ctx.connectionStatus]}
          size="xs"
          text={SESSION_LABEL[ctx.connectionStatus]}
        />
      )}
      {showMic && (
        <RedoBadge
          color={MIC_BADGE_COLOR[ctx.micPermission]}
          size="xs"
          text={MIC_LABEL[ctx.micPermission]}
        />
      )}
      {showRequestMic && (
        <RedoButton
          hierarchy="secondary"
          onClick={ctx.requestMic}
          size="xs"
          text="Request mic"
        />
      )}
    </Flex>
  );
});

const formatDebugNotificationTime = (receivedAt: Date): string =>
  receivedAt.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

const truncateDebugValue = (value: string | null, maxLength = 42): string => {
  if (value == null || value.length === 0) {
    return "-";
  }
  if (value.length <= maxLength) {
    return value;
  }
  return `${value.slice(0, maxLength - 1)}…`;
};

const DebugTelnyxNotificationEvents = memo(
  function DebugTelnyxNotificationEvents() {
    const ctx = useContext(VoiceContextV2);
    const [isOpen, setIsOpen] = useState(false);
    if (ctx == null) {
      throw new Error("VoiceContextV2 missing");
    }

    const notifications = ctx.debugTelnyxNotifications;

    return (
      <>
        <Flex justify="flex-end" w="full">
          <RedoButton
            hierarchy="secondary"
            onClick={() => setIsOpen(true)}
            size="xs"
            text={`events (${notifications.length})`}
          />
        </Flex>
        <RedoModal
          contentScrollable
          hideFooter
          isOpen={isOpen}
          modalSize="md"
          onModalCloseRequested={() => setIsOpen(false)}
          title="Telnyx events"
        >
          <DebugTelnyxNotificationList notifications={notifications} />
        </RedoModal>
      </>
    );
  },
);

const DebugTelnyxNotificationList = memo(function DebugTelnyxNotificationList({
  notifications,
}: {
  readonly notifications: readonly DebugTelnyxNotification[];
}) {
  return (
    <Flex dir="column" gap="xs" w="full">
      {notifications.length === 0 ? (
        <Text fontSize="sm" textColor="secondary">
          No Telnyx events yet.
        </Text>
      ) : (
        notifications.map((notification) => (
          <Flex
            borderColor="secondary"
            borderRadius="md"
            borderStyle="solid"
            borderWidth="1px"
            dir="column"
            gap="xxs"
            key={notification.id}
            p="xs"
            w="full"
          >
            <Flex align="center" gap="xs" justify="space-between" w="full">
              <Text fontFamily="monospace" fontSize="xxs">
                {formatDebugNotificationTime(notification.receivedAt)}
              </Text>
              <RedoBadge
                color={notification.isDebugPing ? "warning" : "gray"}
                size="xs"
                text={notification.isDebugPing ? "debug ping" : "sdk"}
              />
            </Flex>
            <Text fontFamily="monospace" fontSize="xxs" textColor="secondary">
              {notification.type} / {notification.callState ?? "no-state"}
            </Text>
            <Text fontFamily="monospace" fontSize="xxs" textColor="secondary">
              from {truncateDebugValue(notification.fromNumber)}
            </Text>
            <Text fontFamily="monospace" fontSize="xxs" textColor="secondary">
              msg {truncateDebugValue(notification.messageId)}
            </Text>
            <Text fontFamily="monospace" fontSize="xxs" textColor="secondary">
              call {truncateDebugValue(notification.callId)}
            </Text>
            {notification.customHeaders.length > 0 && (
              <Text fontFamily="monospace" fontSize="xxs" textColor="secondary">
                headers{" "}
                {truncateDebugValue(
                  notification.customHeaders
                    .map((h) => `${h.name}=${h.value}`)
                    .join(", "),
                )}
              </Text>
            )}
          </Flex>
        ))
      )}
    </Flex>
  );
});

const CallSection = memo(function CallSection() {
  const ctx = useContext(VoiceContextV2);
  if (ctx == null) {
    throw new Error("VoiceContextV2 missing");
  }

  if (ctx.incoming) {
    return (
      <Flex dir="column" gap="md" p="xl">
        <Text fontSize="xs">
          Incoming call from{" "}
          <Text fontSize="xs" fontWeight="semibold">
            {ctx.incoming.fromNumber ?? "unknown"}
          </Text>
        </Text>
        <Flex gap="xs" justify="center">
          <RedoButton
            hierarchy="primary"
            IconTrailing={PhoneCallIcon}
            onClick={ctx.acceptIncoming}
            text="Answer"
            theme="success"
          />
          <RedoButton
            hierarchy="primary"
            IconTrailing={PhoneXIcon}
            onClick={ctx.rejectIncoming}
            text="Ignore"
            theme="destructive"
          />
        </Flex>
      </Flex>
    );
  }

  if (ctx.activeCall) {
    return <ActiveCallSectionV2 call={ctx.activeCall} />;
  }

  return <PhoneIdleV2 />;
});
